import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { ActivityIndicator } from 'react-native-web';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  VoloFitnessLogoBarbell,
  ProgramTypeEnum,
  HaloChip,
  VoloPassYellowBadge,
} from '@rivallapp/volosports-components';

import classNames from 'classnames/bind';
import PROGRAM_PRICING from './graphql/PROGRAM_PRICING';
import TEAM_PRICING_QUERY from './graphql/TEAM_PRICING_QUERY';
import FeaturesList from './components/FeaturesList';
import pin from '../../../../images/Homepage/DiscoverV3/location.svg';
import level from '../../../../images/Homepage/DiscoverV3/skill_level.svg';
import calendar from '../../../../images/Homepage/DiscoverV3/calendar.svg';
import format_icon from '../../../../images/Homepage/DiscoverV3/format_icon.svg';
import empty_image from '../../../../images/Homepage/DiscoverV3/empty_image.png?as=webp';
import { Picture, Error } from '../../../../components';
import { startCase } from '../../../../shared/string-manipulations';
import { centsToDollars } from '../../../../utils';
import { getPhaseBanner, getScheduleDetails, getLeaguePricingData } from './utilities';
import { useMembershipPrice, useProgramDetails, useMembershipDetails } from '../../hooks';

import styles from '../../../../styles/main.module.scss';

const cx = classNames.bind(styles);

const VPlogo = 'https://rivall-public.s3.us-west-1.amazonaws.com/logos/VoloPass-color.png';

const propTypes = {
  isAnchored: PropTypes.bool,
  programId: PropTypes.string,
  shouldSeePricesWithFees: PropTypes.bool,
};

const defaultProps = {
  programId: '',
  isAnchored: false,
  shouldSeePricesWithFees: false,
};

/**
 * TODO: Eventually remove in favor of ProgramCard
 * Currently in use for League, Tournament, Series, and Volunteer program types
 * @param {Object} props
 * @param {string | undefined} props.programId
 * @param {boolean} [props.shouldSeePricesWithFees]
 * @returns {JSX.Element}
 */
const DiscoverCardOld = ({ programId, isAnchored, shouldSeePricesWithFees }) => {
  const history = useHistory();

  const { isVoloPassActive, isVoloFitnessActive } = useMembershipPrice({ programId });
  const { userIsActiveVoloFitnessMember, userIsActiveVoloPassMember } = useMembershipDetails();

  const {
    loading: programLoading,
    error: programError,
    venue,
    registration,
    gender,
    features,
    formatType,
    banner_text,
    displayName,
    programType,
    programImage,
    hasDivisions,
    price_phase,
    divisionDescriptions,
    num_weeks_estimate,
    start_date,
    end_time_estimate,
    start_time_estimate,
    isPremier,
    isVoloPassExclusive,
  } = useProgramDetails(programId);

  const {
    loading: pricingLoading,
    error: pricingError,
    data: pricingData,
  } = useQuery(PROGRAM_PRICING, {
    skip: !programId,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        leagueId: programId,
        isPrepaidTeam: false,
      },
    },
  });

  const { phases } = registration || {};
  const { earlyBird, regular, lastChance } = phases || {};

  const {
    loading: teamLoading,
    error: teamError,
    data: teamData,
  } = useQuery(TEAM_PRICING_QUERY, {
    skip: !programId,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        leagueId: programId,
        earlyBird: { prepaidTeamPrice: earlyBird?.prepaidTeamPrice || 0 },
        regular: { prepaidTeamPrice: regular?.prepaidTeamPrice || 0 },
        lastChance: { prepaidTeamPrice: lastChance?.prepaidTeamPrice || 0 },
      },
    },
  });

  const { infoLabel, userPrice, basePrice, teamPrice, voloPassPrice, voloFitnessPrice } =
    getLeaguePricingData({
      pricingData,
      isMember: userIsActiveVoloPassMember,
      registration,
      teamData,
      shouldSeePricesWithFees,
    }) || {};

  /** show Volo Fitness pricing and logo on fitness programs (Series):
   * - are in markets that offer Volo Fitness
   * - have a VF discount
   * - only to active VF members
   */
  const showActiveVoloFitnessBranding =
    isVoloFitnessActive &&
    voloFitnessPrice !== undefined &&
    userIsActiveVoloFitnessMember &&
    programType === ProgramTypeEnum.SERIES;

  /** show Volo Pass pricing and logo (blue banner formatting) on programs that:
   * - are in markets that offer Volo Pass
   * - have a VP discount that is less than the base price
   * - to active VP members on all non-Series programTypes regardless of VF status
   * - to active VP members who are NOT VF members on Series programTypes
   */
  const showActiveVoloPassBranding =
    isVoloPassActive && userIsActiveVoloPassMember && basePrice && voloPassPrice;

  const highlightMemberPricing = showActiveVoloFitnessBranding || showActiveVoloPassBranding;

  const membershipPriceLogo = useCallback(
    () =>
      showActiveVoloFitnessBranding ? (
        <VoloFitnessLogoBarbell size={7} colorScheme="primary" />
      ) : (
        <img src={VPlogo} alt="Volo Pass" />
      ),
    [showActiveVoloFitnessBranding]
  );

  const membershipLogo = membershipPriceLogo();

  const { name: venueName = '', location = {} } = venue || {};

  const { lat = 39.299236, lng = -76.609383 } = location || {};

  let photo;
  if (programImage) {
    photo = <Picture src={programImage} alt={`${gender}-${formatType}`} />;
  } else {
    photo = <img src={empty_image} alt={`${gender}-${formatType}`} loading="lazy" />;
  }

  const loading = programLoading || pricingLoading || teamLoading;
  const error = programError || pricingError || teamError;

  if (loading)
    return (
      <div className={cx('loading')}>
        <ActivityIndicator />
      </div>
    );

  return (
    <div
      className={cx('discover-card', isAnchored && 'isAnchored')}
      disabled={loading}
      role="button"
      onClick={() => history.push(`/l/${programId}`)}
      onKeyDown={() => history.push(`/l/${programId}`)}
      tabIndex={0}
    >
      <div className={cx('photo', isVoloPassExclusive && 'volo-pass-exclusive')}>
        {isPremier && (
          <HaloChip
            px={1.5}
            py={0.5}
            top="5px"
            right="5px"
            borderRadius={4}
            position="absolute"
            backgroundColor={{
              linearGradient: {
                colors: ['#FAF001', '#FDF885', '#FFFCB2', '#FCF777', '#FFFCB2', '#FAF006'],
                locations: ['0.3', '0.45', '0.57', '0.64', '0.67', '0.84'],
                start: [0, 0],
                end: [1, 1],
              },
            }}
          >
            <HaloChip.Text bold color="#3f3ea2">
              Premier
            </HaloChip.Text>
          </HaloChip>
        )}
        <div className={cx('program-image')}>{photo}</div>
        {isVoloPassExclusive && (
          <div className={cx('photo-footer')}>
            <VoloPassYellowBadge size={4} mb={1} mr={1} />
            <p className={cx('photo-footer-text')}>Member Exclusive</p>
          </div>
        )}
      </div>

      <div className={cx('name')}>{displayName}</div>

      <div className={cx('details', 'address')}>
        <div className={cx('icon')}>
          <img src={pin} alt="pin" />
        </div>
        <a
          href={`https://www.google.com/maps/place/${lat},${lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cx('googleMapLink')}
        >
          <p>{venueName}</p>
        </a>
      </div>

      <div className={cx('details', 'timing')}>
        <div className={cx('icon')}>
          <img src={calendar} alt="calendar" />
        </div>
        <p>
          {getScheduleDetails({
            num_weeks_estimate,
            start_date,
            end_time_estimate,
            start_time_estimate,
          })}
        </p>
      </div>

      <div className={cx('details', 'format')}>
        <div className={cx('icon')}>
          <img src={format_icon} alt="format_icon" />
        </div>
        <p>
          {startCase(gender)} {formatType !== 'N/A' && formatType}
        </p>
      </div>

      <div className={cx('details', 'skill-levels')}>
        <div className={cx('icon')}>
          <img src={level} alt="skill level" />
        </div>
        <p>
          Skill Level -{' '}
          {hasDivisions
            ? divisionDescriptions.map(
                (division, index) =>
                  `${index !== divisionDescriptions.length - 1 ? `${division}, ` : division}`
              )
            : 'All'}
        </p>
      </div>

      <div className={cx('details', 'features')}>
        <FeaturesList
          venueFeatures={features || []}
          leagueId={programId}
          isVoloPassExclusive={isVoloPassExclusive}
        />
      </div>

      <div className={cx('details', 'banners')}>
        {phases && price_phase && banner_text && (
          <div className={cx('banner-message')}>{banner_text}</div>
        )}
        <div className={cx('banner-phase')}>{getPhaseBanner({ price_phase, phases })}</div>
      </div>
      <div className={cx('pricing')}>
        {error && <Error error={pricingError || teamError} />}
        {highlightMemberPricing ? (
          <div className={cx('VPlabel')}>
            <div className={cx('VPlogo')}>{membershipLogo}</div>
            <p>Membership Pricing!</p>
          </div>
        ) : (
          <div className={cx('infoLabel')}>{infoLabel}</div>
        )}
        <div className={cx('price')}>
          <span className={cx('currentPrice')}>
            $
            {showActiveVoloFitnessBranding
              ? centsToDollars(voloFitnessPrice).toFixed(0)
              : centsToDollars(userPrice).toFixed(2)}
            <span>&nbsp;/&nbsp;person</span>
          </span>
          <span className={cx('basePrice')}>
            {basePrice && `$${centsToDollars(basePrice).toFixed(2)}`}
          </span>
        </div>

        <div className={cx('price')}>
          <span className={cx('teamPrice')}>
            {teamPrice && (
              <>
                {`$${centsToDollars(teamPrice).toFixed(2)}`}
                <span>&nbsp;/&nbsp;team</span>
              </>
            )}
          </span>
        </div>

        {isVoloPassActive && !userIsActiveVoloPassMember && voloPassPrice != null && (
          <div className={cx('member-pricing')}>
            <span>Membership Pricing</span>
            <span className={cx('original-price')}>
              {basePrice
                ? `$${centsToDollars(basePrice).toFixed(2)}`
                : `$${centsToDollars(userPrice).toFixed(2)}`}
            </span>
            <span className={cx('member-price')}>{`$${centsToDollars(voloPassPrice).toFixed(
              2
            )}`}</span>
          </div>
        )}
      </div>
    </div>
  );
};

DiscoverCardOld.propTypes = propTypes;
DiscoverCardOld.defaultProps = defaultProps;

export default DiscoverCardOld;
